/**
 * SUPPLY-CHAIN DISRUPTION CALENDAR — sourced holiday data with provenance.
 *
 * Brian: "In the calendar, I want you to highlight every major Chinese holiday,
 * that's JD, and every major Ethiopian holiday, that's SRE. That way we know
 * when there might be a disruption. Overlay the major American holidays as
 * well." And: "This should show up in command center."
 *
 * ⚠️ THE OFFICIAL HOLIDAY IS NOT THE DISRUPTION WINDOW, and the window is the
 * number SR actually needs. A public holiday is one or three days; the
 * production stoppage around Chinese New Year is weeks. So EVERY entry carries
 * TWO date ranges that are never merged:
 *
 *     official   what the government declares. Statutory.
 *     window     when work is actually degraded or stopped. Operational.
 *
 * `window.status` is the load-bearing field:
 *
 *     SOURCED    a real source states this window. Cite it.
 *     FORECAST   a named source projects it from an unpublished anchor.
 *     UNKNOWN    nobody states it. Renders as UNKNOWN WITH ITS REASON and is
 *                never silently dropped, never defaulted to the official dates,
 *                and NEVER rendered as a zero-length or guessed range.
 *
 * A guessed window shown as fact is worse than no window at all, because
 * somebody will plan a ship date against it. There is no code path in this file
 * that invents a window from a lead time, a lunar table, or an average.
 *
 * ⚠️ DATES ARE SOURCED, NOT RECALLED OR COMPUTED. Chinese holidays are lunar
 * and move every year; Ethiopia runs the Ge'ez calendar (13 months, different
 * year number) with Orthodox Easter reckoning and lunar Islamic holidays; US
 * federal holidays shift when they land on a weekend. Nothing here is derived
 * from an nth-weekday rule or a lunar library. Every date was read off a
 * retrieved page, and the page's own date stamp is recorded in `dated` —
 * because THE OFFICIAL PAGE CAN BE THE STALE ONE.
 *
 * Research run: 2026-10-04. Hand research on the existing subscription.
 * No paid holiday/search API was used. No spend.
 *
 * COVERAGE: Gregorian 2026 and 2027. SR's season convention is Jul→Jun, so the
 * current season is 2026-2027 and this carries through the end of 2027.
 *
 * ⚠️ WHAT THIS FILE IS NOT: it is not JD's or SRE's own holiday schedule. Every
 * China source independently says the published calendar is not the factory's
 * calendar — Silk Way Sourcing: "Get each factory's annual holiday schedule in
 * writing, and re-ask every year." JD's and SRE's written schedules are a
 * REQUIRED INPUT THAT SR DOES NOT CURRENTLY HOLD. See `KNOWN_GAPS`.
 */

/* ---- lanes -------------------------------------------------------------- */

/* Tagged to WHO it disrupts, which is the whole point — a Chinese shutdown
 * stops JD's production, an Ethiopian holiday stops SRE's artwork, a US holiday
 * stops the carrier. Short codes because they have to fit a 390px day cell.
 *
 * ⚠️ COLOUR IS NEVER THE ONLY CARRIER OF MEANING. Each lane has a code, a
 * glyph and a full label, and the UI renders at least the code or glyph
 * alongside any colour. Three overlays on a phone is a real design problem and
 * colour alone would make it a soup. */
export const LANES = {
  JD: {
    code: 'JD',
    glyph: '▲',
    label: 'JD — China',
    what: 'Manufacturing. A shutdown here stops production.',
  },
  SRE: {
    code: 'SRE',
    glyph: '■',
    label: 'SRE — Ethiopia',
    what: 'Artwork and design. A holiday here stops proofs and art.',
  },
  US: {
    code: 'US',
    glyph: '●',
    label: 'US — domestic',
    what: 'Carriers and the floor. A closure here stops shipping.',
  },
}

/* ---- severity ----------------------------------------------------------- */

/* A full multi-week factory shutdown is not the same thing as a single public
 * holiday where some work continues, and flattening them would make the whole
 * overlay useless. `rank` orders them so a day carrying several disruptions
 * reports its WORST one. */
export const SEVERITY = {
  FULL_SHUTDOWN: { rank: 4, label: 'Full shutdown', short: 'SHUTDOWN', blurb: 'Multi-week stoppage. Do not promise a date across this.' },
  HARD_STOP: { rank: 3, label: 'Hard stop', short: 'HARD STOP', blurb: 'Everything closed for several days, then a backlog.' },
  MULTI_DAY: { rank: 2, label: 'Multi-day closure', short: 'MULTI-DAY', blurb: 'Several days closed. Enough to matter to a tight schedule.' },
  SINGLE_DAY: { rank: 1, label: 'Single day', short: '1 DAY', blurb: 'One day off. Work resumes the next working day.' },
  LIMITED: { rank: 1, label: 'Limited impact', short: 'LIMITED', blurb: 'Observed, but work and dispatch largely continue.' },
  UNKNOWN: { rank: 0, label: 'Severity unknown', short: 'UNKNOWN', blurb: 'Not established. Treat as unquantified risk, not as safe.' },
}

/* ---- sources ------------------------------------------------------------ */

/* Provenance lives HERE, once, keyed — not scattered through component code and
 * not repeated per entry. `dated` is whether the PAGE carried a date or season
 * stamp, recorded separately from whether the publisher is official, because an
 * official page with no stamp is an undated page. */
export const SOURCES = {
  // ---- China, official ----
  CN_SC_2026: {
    publisher: 'State Council General Office (国务院办公厅), republished by Beijing municipal government',
    doc: '国办发明电〔2025〕7号 — 关于2026年部分节假日安排的通知',
    url: 'https://www.beijing.gov.cn/zhengce/zhengcefagui/202511/t20251104_4258873.html',
    dated: '2025-11-04 (成文日期 and 发布日期 both stamped on the document metadata block)',
    official: true,
  },
  CN_GAZETTE_2026: {
    publisher: 'State Council Gazette (国务院公报 2025年第32号)',
    doc: 'Gazette republication of the 2026 holiday arrangement',
    url: 'http://big5.www.gov.cn/gate/big5/www.gov.cn/gongbao/2025/issue_12406/202511/content_7048922.html',
    dated: '2025 issue 32 — stamped',
    official: true,
  },
  CN_GOV_EN_2026: {
    publisher: 'english.gov.cn',
    doc: "China's public holidays for 2026",
    url: 'https://english.www.gov.cn/policies/featured/202511/04/content_WS6909c915c6d00ca5f9a074f7.html',
    dated: '2025-11-04 ("Updated: November 4, 2025")',
    official: true,
    caveat: '⚠️ The holiday table on this page is a JPEG image, not text. Unusable as a machine-readable source — the Chinese text above is the citable one.',
  },
  // ---- China, disruption window ----
  MIT_CTL: {
    publisher: 'MIT Center for Transportation & Logistics — Dr. Jay Guo, Dean, MIT Global SCALE Network Ningbo China Institute for Supply Chain Innovation',
    doc: 'Chinese New Year and its Impact on Global Supply Chain',
    url: 'https://ctl.mit.edu/news/chinese-new-year-and-its-impact-global-supply-chain',
    dated: '2025-01-28 (bylined and dated)',
    official: false,
    quote: '"In general, factories across China halt operations for at least 7-10 days, with many shutting down even longer for 2-3 weeks."',
    caveat: 'Written for CNY 2025. The mechanism is year-independent; the dates are not.',
  },
  TITOMA: {
    publisher: 'Titoma (ODM operating in Shenzhen)',
    doc: 'Chinese New Year & Golden Week: China Factory Shutdowns',
    url: 'https://titoma.com/blog/chinese-new-year-affect-manufacturing/',
    dated: 'published 2020-10-10, modified 2026-10-01',
    official: false,
    quote: '"many factories slow down from early February and only return to normal output in early to mid March"',
    caveat: 'Deliberately mixed-vintage: its own banner says Taiwan dates are 2027 while "China sections retain the 2026 information". Do not read its Taiwan table as China data.',
  },
  OLIMP: {
    publisher: 'OLIMP Warehousing (3PL)',
    doc: 'Planning for Chinese New Year 2026',
    url: 'https://olimpwarehousing.com/planning-chinese-new-year-2026-logistics/',
    dated: '2026-01-12',
    official: false,
    quote: '"importers must plan for a 3-4 week gap in production, not just one week"; reopening at "reduced capacity (often 30-50%)"',
    caveat: '⚠️ CONTRADICTS THE STATE COUNCIL ON THE OFFICIAL DATES — its lead and FAQ say "Feb 17-23" while its own body timeline says "Feb 15-23". Feb 17 is lunar New Year\'s Day, not the start of the holiday block. Cited for the WINDOW ONLY, never for dates.',
  },
  SEKO: {
    publisher: 'SEKO Logistics (freight forwarder)',
    doc: 'Lunar New Year: complete supply-chain planning guide for Asia sourcing',
    url: 'https://www.sekologistics.com/en/resource-hub/knowledge-hub/lunar-new-year-complete-supply-chain-planning-guide-for-asia-sourcing/',
    dated: '~2026-08 (body text self-references "publication in early August 2026"; no explicit byline date)',
    official: false,
    quote: '"China\'s State Council... typically confirms the following year\'s Spring Festival arrangement in November or December, meaning the exact length of China\'s 2027 holiday block won\'t be official until roughly two months before the holiday itself."',
    caveat: '⚠️ URL IS UNSTABLE. The 2026 edition 301-redirects to this rewritten 2027 edition; the 2026 content is gone. Retrieval date matters for this one.',
  },
  SILKWAY: {
    publisher: 'Silk Way Sourcing (team based in Jinan)',
    doc: "Golden Week and China's Other Shutdowns",
    url: 'https://www.silkwaysourcing.com/blog/china-holidays-production-shutdowns',
    dated: '2026-08-24 (bylined Arkadii Vakhnovskyi, published editorial policy)',
    official: false,
    quote: '"Ports keep running, everything around them does not." / "Add buffer proportional to the break — a few days for Qingming, weeks for Chinese New Year."',
  },
  SORVA: {
    publisher: 'Sorva (sourcing/trading house, Guangzhou office)',
    doc: 'China Golden Week 2026: dates, factory closures and freight',
    url: 'https://sorvatrade.com/en/guide/golden-week-octobre-2026-usines',
    dated: '2026-09-26 ("Updated September 26, 2026")',
    official: false,
    quote: '"Production stops completely from 1 to 7 October 2026 and the factory\'s usual contacts are unreachable." / "the reopening on 8 October does not immediately restore transport schedules."',
  },
  // ---- Ethiopia ----
  ET_PROC_1334: {
    publisher: 'Federal Democratic Republic of Ethiopia — Proclamation No. 1334/2024 (Public Holidays)',
    doc: 'Proclamation 1334/2024, English text. Art. 4 national holidays, Art. 6 religious holidays, Art. 19 repeals the prior regime',
    url: 'https://www.metaappz.com/References/ethiopian_laws/federal/pr_1334_2024/en/txt',
    dated: 'signed 2024-08-14 (date in the text)',
    official: true,
    caveat: '⚠️ THIS, NOT PROCLAMATION 29/1996, IS THE GOVERNING LAW. Most aggregators and Wikipedia still cite the repealed 29/1996. This transcription is third-party with its own accuracy disclaimer, not a Negarit Gazette scan.',
  },
  ET_TIME: {
    publisher: 'time.et (Ethiopian-authored holiday reference)',
    doc: 'Ethiopian Public Holidays, dual Ethiopian/Gregorian',
    url: 'https://time.et/holidays',
    dated: '© 2026',
    official: false,
    caveat: '⚠️ The copy retrieved was a cached snapshot reading "Today · September 25, 2026" against a research date of 2026-10-04 — roughly 9 days stale — and it covers Sept 2026 onward only, not Jan-Aug 2026. Marks Islamic dates "± 1 day" itself.',
  },
  ET_UNECA: {
    publisher: 'UN Economic Commission for Africa, UN Conference Centre Addis Ababa',
    doc: 'UN Holidays (the closure list for a real Addis-based office)',
    url: 'https://www.uneca.org/united-nations-conference-centre-in-addis-ababa-(uncc-aa)/un-holidays',
    dated: 'stamped 2026 throughout',
    official: false,
    caveat: "Partial list only — the UN's own 10-day allotment omits Timkat, Adwa, Fasika, May 1, May 5, May 28 and Meskel. Good for date corroboration, useless for completeness. Carries the footnote \"Dates may change based on the lunar phase.\"",
  },
  ET_DE_EMBASSY: {
    publisher: 'German Embassy Addis Ababa',
    doc: 'Public Holidays',
    url: 'https://addis-abeba.diplo.de/et-en/about-us/public-holidays-2629598',
    dated: 'maintains a 2026 table only — currently maintained',
    official: true,
    caveat: 'Partial: German + selected Ethiopian days. Omits Adwa, Fasika and all Islamic holidays as a function of German embassy policy, not Ethiopian law.',
  },
  ET_ENA_TIMKAT: {
    publisher: 'Ethiopian News Agency (state news agency)',
    doc: 'Timket: A Unique Ethiopian Cultural Festival',
    url: 'https://www.ena.et/web/eng/w/en_5529',
    dated: '⚠️ UNDATED. Internal references to a UNESCO inscription "expected" Dec 2019 place the text in 2019; page footer © 2023. Treat as undated.',
    official: true,
  },
  TIMEANDDATE_ET: {
    publisher: 'timeanddate.com',
    doc: 'Ethiopia holidays 2026 / 2027',
    url: 'https://www.timeanddate.com/holidays/ethiopia/2026',
    dated: 'per-year pages',
    official: false,
    caveat: 'Aggregator. Used as corroboration only, never as a primary. Labels Islamic dates "Tentative Date".',
  },
  // ---- United States ----
  OPM: {
    publisher: 'US Office of Personnel Management',
    doc: 'Federal Holidays — published per-year schedules',
    url: 'https://www.opm.gov/policy-data-oversight/pay-leave/federal-holidays/',
    dated: 'YES — per-year tabs headed "2026 Holiday Schedule" and "2027 Holiday Schedule"; each cites 5 U.S.C. 6103(b) and E.O. 11582',
    official: true,
    note: 'OPM HAS published 2026 AND 2027 (and 2028-29). So no US date here is rule-derived — both years are read off a published schedule.',
  },
  USPS_2026: {
    publisher: 'USPS',
    doc: '2026 holidays',
    url: 'https://about.usps.com/newsroom/events/',
    dated: 'YES — heading reads "2026 holidays"',
    official: true,
  },
  USPS_JUL4: {
    publisher: 'USPS national release',
    doc: 'USPS will be closed in observance of Independence Day July 4',
    url: 'https://about.usps.com/newsroom/national-releases/2026/0626-usps-will-be-closed-in-observance-of-independence-day-july-4.htm',
    dated: '2026-06-26',
    official: true,
    quote: '"All Post Office locations will be closed [Sat July 4]... Post Offices will be open, and deliveries will occur as normal on Friday, July 3."',
  },
  UPS_2026: {
    publisher: 'UPS',
    doc: '2026 U.S. UPS Holiday Operations Schedule',
    url: 'https://assets.ups.com/adobe/assets/urn:aaid:aem:4756cbe3-c3b7-4bfb-a79e-1d285308dc2e/original/as/2026-holiday-operations-schedule-us-en.pdf',
    dated: 'YES — titled for 2026',
    official: true,
    quote: '"UPS is closed on New Year\'s Day, Martin Luther King Jr. Day, Memorial Day, Independence Day, Labor Day, Thanksgiving, and Christmas Day."',
  },
  FEDEX_2026: {
    publisher: 'FedEx',
    doc: '2026 FedEx holiday service schedule (parcel/express)',
    url: 'https://www.fedex.com/content/dam/fedex/us-united-states/services/2026_Holiday_Operations_Schedule.pdf',
    dated: 'YES — 2026',
    official: true,
    caveat: 'Scope, verbatim: "FedEx includes all U.S. and international parcel and express freight services, except LTL freight services." LTL diverges and must be read separately.',
  },
  FEDEX_FREIGHT_2026: {
    publisher: 'FedEx Freight (LTL)',
    doc: '2026 holiday schedule',
    url: 'https://www.fedexfreight.com/en-us/rate-ship/shipping/holiday-schedule',
    dated: 'YES — "Plan ahead for every shipping season in 2026"; meta-modified 2026-08-26',
    official: true,
    quote: '"a one-day shipment picked up on Dec. 23 will resume on Dec. 28 and be delivered on Dec. 29"',
  },
  FED_G17: {
    publisher: 'US Federal Reserve, G.17 Industrial Production technical Q&A',
    doc: 'Production-day methodology',
    url: 'https://www.federalreserve.gov/releases/g17/g17_technical_qa.htm',
    dated: 'standing methodology page',
    official: true,
    quote: '"Taking into account holidays and the shutdowns in the summer and at the end of the year, there are 235 production days in a typical model year."',
  },
}

/* ---- the data ----------------------------------------------------------- */

/**
 * Every entry:
 *   lane      JD | SRE | US
 *   name      English. `localName` where there is one.
 *   year      Gregorian. ⚠️ THE DATE INCLUDES THE YEAR — nothing here is a
 *             recurring fixture, because almost none of these repeat on a date.
 *   lunar     true where the date MOVES year to year, so nobody treats a
 *             2026 date as next year's.
 *   official  { start, end, status, source, note }
 *             status: OFFICIAL | OBSERVED | ESTIMATE | FORECAST | DISPUTED | UNKNOWN
 *   window    { start, end, status, sources, reason }
 *             status: SOURCED | FORECAST | UNKNOWN
 *   severity  key into SEVERITY
 *   confidence high | medium | low
 *
 * Dates are ISO `YYYY-MM-DD` strings and are read as LOCAL calendar days, never
 * parsed through UTC — same rule as `dayKey` in lib/championships.js, and for
 * the same reason: west of Greenwich `new Date('2026-02-15')` lands on Feb 14.
 */
export const HOLIDAYS = [
  /* ===================== JD — CHINA — 2026 (OFFICIAL) =====================
   * All seven 2026 blocks come from one published notice, 国办发明电〔2025〕7号,
   * dated 2025-11-04. This is the arrangement China actually published, not a
   * lunar computation. */
  {
    id: 'cn-2026-newyear', lane: 'JD', year: 2026, lunar: false,
    name: 'New Year', localName: '元旦',
    official: { start: '2026-01-01', end: '2026-01-03', status: 'OFFICIAL', source: 'CN_SC_2026', note: '3 days. Make-up working day: Sun 2026-01-04.' },
    window: { start: '2026-01-01', end: '2026-01-03', status: 'SOURCED', sources: ['SILKWAY'], note: 'Silk Way: "1 January, one to three days. Minor, but it stops document flow."' },
    severity: 'LIMITED', confidence: 'high',
  },
  {
    id: 'cn-2026-springfestival', lane: 'JD', year: 2026, lunar: true,
    name: 'Spring Festival / Chinese New Year', localName: '春节',
    official: { start: '2026-02-15', end: '2026-02-23', status: 'OFFICIAL', source: 'CN_SC_2026', note: '9 days — a record length. 除夕 CNY Eve 2026-02-16, 正月初一 CNY Day 2026-02-17. Make-up working days: Sat 2026-02-14 and Sat 2026-02-28.' },
    window: {
      start: '2026-02-01', end: '2026-03-16', status: 'SOURCED',
      sources: ['MIT_CTL', 'TITOMA', 'OLIMP'],
      note: 'THE WINDOW IS ~6 WEEKS, NOT 9 DAYS. MIT CTL: factories "halt operations for at least 7-10 days, with many shutting down even longer for 2-3 weeks". Titoma: "slow down from early February and only return to normal output in early to mid March". OLIMP: "a 3-4 week gap in production, not just one week", reopening at 30-50% capacity. Sources also report 25-33% of workers do not return to the same employer, which degrades quality on both sides of the break.',
      spread: '⚠️ Sources disagree on the boundaries and are partly measuring different things (hard stop vs degraded-output envelope): 2-3 weeks (MIT), 3-4 weeks (OLIMP), "a 6-8 week event" (OLIMP), 4-6 weeks to full recovery (SEKO). These have NOT been averaged. No source gives a defensible exact start or end day — all of them say to ask the individual factory.',
    },
    severity: 'FULL_SHUTDOWN', confidence: 'high',
    confidenceNote: 'HIGH on the official dates. MEDIUM on the window boundaries — the multi-week character is certain, the exact days are not.',
  },
  {
    id: 'cn-2026-qingming', lane: 'JD', year: 2026, lunar: true,
    name: 'Qingming', localName: '清明节',
    official: { start: '2026-04-04', end: '2026-04-06', status: 'OFFICIAL', source: 'CN_SC_2026', note: '3 days. No make-up working day. Solar-term date — moves year to year.' },
    window: { start: '2026-04-04', end: '2026-04-06', status: 'SOURCED', sources: ['SILKWAY'], note: 'Silk Way: "early April, typically three days", buffer "a few days for Qingming". No source claims extended stoppage.' },
    severity: 'MULTI_DAY', confidence: 'high',
  },
  {
    id: 'cn-2026-labour', lane: 'JD', year: 2026, lunar: false,
    name: 'Labour Day', localName: '劳动节',
    official: { start: '2026-05-01', end: '2026-05-05', status: 'OFFICIAL', source: 'CN_SC_2026', note: '5 days. Make-up working day: Sat 2026-05-09.' },
    window: {
      start: '2026-05-01', end: '2026-05-05', status: 'SOURCED', sources: ['SILKWAY'],
      note: 'Silk Way: "commonly a five-day break in recent years. Big enough to matter for a tight schedule."',
      shoulderStatus: 'UNKNOWN',
      shoulderReason: 'One source claims a 9-day practical window against the 5 official days, but that source (pinjia.day) is UNDATED, UNAUTHORED and internally self-contradictory, so it is not relied on. Any stoppage beyond May 1-5 is UNKNOWN.',
    },
    severity: 'MULTI_DAY', confidence: 'high',
  },
  {
    id: 'cn-2026-dragonboat', lane: 'JD', year: 2026, lunar: true,
    name: 'Dragon Boat', localName: '端午节',
    official: { start: '2026-06-19', end: '2026-06-21', status: 'OFFICIAL', source: 'CN_SC_2026', note: '3 days. No make-up working day. Lunar — moves.' },
    window: { start: '2026-06-19', end: '2026-06-21', status: 'SOURCED', sources: ['SILKWAY'], note: 'Silk Way: "around three days". No sourced evidence of extension.' },
    severity: 'MULTI_DAY', confidence: 'high',
  },
  {
    id: 'cn-2026-midautumn', lane: 'JD', year: 2026, lunar: true,
    name: 'Mid-Autumn', localName: '中秋节',
    official: { start: '2026-09-25', end: '2026-09-27', status: 'OFFICIAL', source: 'CN_SC_2026', note: '3 days. No make-up working day. Lunar — moves.' },
    window: {
      start: '2026-09-25', end: '2026-10-10', status: 'SOURCED', sources: ['SORVA', 'SILKWAY'],
      note: '⚠️ COMPOUNDS INTO GOLDEN WEEK IN 2026. Mid-Autumn and National Day are NOT merged this year, but only three working days separate them — Sep 28, 29, 30. Sorva confirms the gap and notes it "concentrates transport demand into just a few days". Treat Sep 25 → Oct 10 as one disrupted block rather than two short ones. Silk Way flags exactly this: "In some years it runs into Golden Week, producing an unusually long combined break — worth checking each year."',
    },
    severity: 'MULTI_DAY', confidence: 'high',
  },
  {
    id: 'cn-2026-nationalday', lane: 'JD', year: 2026, lunar: false,
    name: 'National Day / Golden Week', localName: '国庆节',
    official: { start: '2026-10-01', end: '2026-10-07', status: 'OFFICIAL', source: 'CN_SC_2026', note: '7 days. Oct 1 is fixed, but the surrounding shutdown is not. Make-up working days: Sun 2026-09-20 and Sat 2026-10-10.' },
    window: {
      start: '2026-09-28', end: '2026-10-10', status: 'SOURCED', sources: ['SORVA', 'SILKWAY', 'TITOMA'],
      note: 'Sorva: "Production stops completely from 1 to 7 October 2026 and the factory\'s usual contacts are unreachable"; official reopening Thu Oct 8, but "the reopening on 8 October does not immediately restore transport schedules". Silk Way: "Factories close for the full week... Logistics slows for two to three days on either side as trucking and ports drain and refill", and orders needing to ship before Golden Week "need to be placed roughly four to six weeks ahead". Unlike CNY there is no workforce rotation — Sorva: "most teams return to their posts as soon as it reopens."',
    },
    severity: 'HARD_STOP', confidence: 'high',
  },

  /* ===================== JD — CHINA — 2027 (NOT PUBLISHED) ================
   * 🔴 THE GOVERNING NOTICE DOES NOT EXIST YET. China's State Council publishes
   * the annual 放假安排 in Nov/Dec for the following year; as of 2026-10-04 no
   * 2027 notice has been published. Corroborated by SEKO, which marks China
   * Mainland 2027 "Provisional" and says the block "won't be official until
   * roughly two months before the holiday itself".
   *
   * ⚠️ These entries are deliberately PRESENT AND UNKNOWN rather than dropped
   * or computed. Dropping them would make the 2027 calendar look safe. Only the
   * two statutory FIXED anchors (Jan 1, Oct 1) are known, and even for those
   * the BLOCK LENGTH is unknown — which is the operationally relevant part. */
  {
    id: 'cn-2027-newyear', lane: 'JD', year: 2027, lunar: false,
    name: 'New Year', localName: '元旦',
    official: { start: '2027-01-01', end: '2027-01-01', status: 'UNKNOWN', source: 'SEKO', note: 'Jan 1 is statutory and fixed, but the number of days off and any make-up working weekend are UNKNOWN — the 2027 State Council notice is not published.' },
    window: { status: 'UNKNOWN', reason: 'The 2027 holiday arrangement has not been published. Expected Nov/Dec 2026.' },
    severity: 'UNKNOWN', confidence: 'low',
  },
  {
    id: 'cn-2027-springfestival', lane: 'JD', year: 2027, lunar: true,
    name: 'Spring Festival / Chinese New Year', localName: '春节',
    official: {
      start: '2027-02-06', end: '2027-02-06', status: 'FORECAST', source: 'SEKO',
      note: '🔴 OFFICIAL BLOCK UNKNOWN — notice not published. 正月初一 CNY Day is forecast as Sat 2027-02-06 by four independent trade sources (SEKO, Sorva, Silk Way, Titoma), with 除夕 CNY Eve Fri 2027-02-05. That is an ANCHOR DATE, not a holiday block: how many days JD actually closes, and which weekends become make-up working days, is not knowable yet.',
    },
    window: {
      start: '2027-01-30', end: '2027-03-20', status: 'FORECAST', sources: ['SEKO'],
      reason: 'FORECAST, NOT SOURCED — derived by SEKO from a provisional Feb 6 anchor, by its own admission. Do not plan a ship date against it.',
      note: "SEKO's phase model off the provisional anchor: pre-holiday slowdown from ~Jan 9-16; complete shutdown ~Jan 30 - Feb 14; gradual resumption ~Feb 20 - Mar 6 at \"only about 35% workforce capacity\"; full recovery ~Mar 6-20. Also: \"Many facilities stop accepting new orders 6-8 weeks before CNY.\"",
    },
    severity: 'FULL_SHUTDOWN', confidence: 'low',
    confidenceNote: 'The MECHANISM is certain — there will be a multi-week shutdown. Every DATE is low confidence. Re-run this entry once the State Council notice publishes (~Nov/Dec 2026).',
  },
  {
    id: 'cn-2027-qingming', lane: 'JD', year: 2027, lunar: true,
    name: 'Qingming', localName: '清明节',
    official: { status: 'UNKNOWN', note: 'Notice not published. A ~Apr 5 2027 anchor appears in search-result summaries only and was NOT primary-verified; the Hong Kong Observatory 2027 almanac PDF — the cleanest government-grade lunar source — was blocked, and gov.hk\'s 2027 holiday page returned an empty body. Not asserting a date.' },
    window: { status: 'UNKNOWN', reason: 'No published 2027 arrangement, and the underlying lunar date is not primary-verified.' },
    severity: 'UNKNOWN', confidence: 'low',
  },
  {
    id: 'cn-2027-labour', lane: 'JD', year: 2027, lunar: false,
    name: 'Labour Day', localName: '劳动节',
    official: { start: '2027-05-01', end: '2027-05-01', status: 'UNKNOWN', note: 'May 1 is statutory and fixed; the block length (5 days in 2026) and make-up weekends are UNKNOWN pending the 2027 notice.' },
    window: { status: 'UNKNOWN', reason: 'The 2027 holiday arrangement has not been published.' },
    severity: 'UNKNOWN', confidence: 'low',
  },
  {
    id: 'cn-2027-dragonboat', lane: 'JD', year: 2027, lunar: true,
    name: 'Dragon Boat', localName: '端午节',
    official: { status: 'UNKNOWN', note: 'Notice not published. Titoma\'s Taiwan table gives a 2027-06-09 lunar anchor; that is Taiwan data on a page whose China sections are 2026, so it is not asserted as the PRC date.' },
    window: { status: 'UNKNOWN', reason: 'No published 2027 arrangement; lunar anchor not primary-verified for the PRC.' },
    severity: 'UNKNOWN', confidence: 'low',
  },
  {
    id: 'cn-2027-midautumn', lane: 'JD', year: 2027, lunar: true,
    name: 'Mid-Autumn', localName: '中秋节',
    official: { status: 'UNKNOWN', note: 'Notice not published. A 2027-09-15 anchor appears in Titoma\'s Taiwan table and search summaries; not primary-verified for the PRC. ⚠️ Whether it compounds into Golden Week in 2027 — as it nearly does in 2026 — cannot be determined until the notice publishes.' },
    window: { status: 'UNKNOWN', reason: 'No published 2027 arrangement; lunar anchor not primary-verified.' },
    severity: 'UNKNOWN', confidence: 'low',
  },
  {
    id: 'cn-2027-nationalday', lane: 'JD', year: 2027, lunar: false,
    name: 'National Day / Golden Week', localName: '国庆节',
    official: { start: '2027-10-01', end: '2027-10-01', status: 'UNKNOWN', source: 'SORVA', note: 'Oct 1 is fixed — Sorva: "this date stays fixed year on year". The 7-day block and its make-up weekends are UNKNOWN pending the 2027 notice.' },
    window: { status: 'UNKNOWN', reason: 'The 2027 holiday arrangement has not been published. The fixed Oct 1 date does not establish the shutdown around it.' },
    severity: 'UNKNOWN', confidence: 'low',
  },

  /* ===================== SRE — ETHIOPIA — 2026 ============================
   * Governing law is Proclamation No. 1334/2024, which repeals the 29/1996
   * regime most aggregators still cite. It lists twelve closure holidays: four
   * national (Art. 4) and eight religious (Art. 6) — and the eight religious
   * ones include all three Islamic holidays, as NATIONAL closures regardless of
   * an employee's faith (Art. 6(9)).
   *
   * ⚠️ The disruption profile here is genuinely different from China's: the
   * evidence supports INDIVIDUAL DAYS OFF, not multi-week shutdowns. No source
   * found establishes extended operational stoppage around any Ethiopian
   * holiday. Do not carry over the CNY mental model.
   *
   * Ethiopian calendar: Meskerem 1 of EC 2019 = 2026-09-11; EC 2020 = 2027-09-12. */
  {
    id: 'et-2026-genna', lane: 'SRE', year: 2026, lunar: false, ethiopianYear: 'EC 2018 (Tahsas 29)',
    name: 'Genna — Ethiopian Orthodox Christmas', localName: 'ገና',
    official: { start: '2026-01-07', end: '2026-01-07', status: 'OFFICIAL', source: 'ET_PROC_1334', note: 'Fixed by Proclamation 1334/2024 Art. 6(2). Corroborated by the German Embassy 2026 table and UNECA 2026.' },
    window: { status: 'UNKNOWN', reason: 'No source establishes an extended stoppage. Genna closes the 43-day Advent fast (ጾመ ነቢያት), which is a DIETARY observance, not a work stoppage — and is not treated as a window here.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2026-timkat', lane: 'SRE', year: 2026, lunar: false, ethiopianYear: 'EC 2018 (Tir 11)',
    name: 'Timkat — Epiphany', localName: 'ጥምቀት',
    official: { start: '2026-01-19', end: '2026-01-19', status: 'OFFICIAL', source: 'ET_PROC_1334', note: 'Art. 6(3). Only Jan 19 is a legal closure day.' },
    window: {
      start: '2026-01-18', end: '2026-01-20', status: 'SOURCED', sources: ['ET_ENA_TIMKAT'],
      note: 'A 3-day festival — Ketera (eve) on the 18th, the main blessing on the 19th, tabots returned on the 20th. Only the 19th is statutory; the 18th and 20th are PARTIAL at most.',
      caveat: '⚠️ The window source (ENA) is UNDATED, and no source establishes the 18th or 20th as a non-working day. The 3-day festival structure is calendrically fixed; its effect on attendance is not sourced.',
    },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2026-adwa', lane: 'SRE', year: 2026, lunar: false, ethiopianYear: 'EC 2018 (Yekatit 23)',
    name: 'Adwa Victory Day', localName: 'የዓድዋ ድል በዓል',
    official: { start: '2026-03-02', end: '2026-03-02', status: 'OFFICIAL', source: 'ET_PROC_1334', note: 'Art. 4(2) and Art. 10 — statutory March 02.' },
    window: { status: 'UNKNOWN', reason: 'No sourced evidence of any extension. A state ceremony with a 6:00am cannon salute is a ceremony, not a shutdown.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2026-eidalfitr', lane: 'SRE', year: 2026, lunar: true, ethiopianYear: 'EC 2018',
    name: 'Eid al-Fitr', localName: 'ኢድ አልፈጥር',
    official: {
      start: '2026-03-20', end: '2026-03-20', status: 'ESTIMATE', source: 'ET_UNECA',
      note: '⚠️ ESTIMATE, ±1 DAY, SUBJECT TO MOON SIGHTING. 1 Shawwal 1447. Statutory holiday under Art. 6(7). UNECA (2026-stamped, Addis office) gives Mar 20 with the footnote "Dates may change based on the lunar phase"; timeanddate labels it "Tentative Date".',
      contradiction: 'holidayapi gives 2026-03-19. Source spread is 19-21 March. Mar 20 is reported as the central estimate with the disagreement recorded, not resolved.',
    },
    window: { status: 'UNKNOWN', reason: 'No source establishes a multi-day stoppage. Nothing found on whether the preceding Ramadan fast affects output in Ethiopia, and no window is estimated from it.' },
    severity: 'SINGLE_DAY', confidence: 'medium',
  },
  {
    id: 'et-2026-goodfriday', lane: 'SRE', year: 2026, lunar: true, ethiopianYear: 'EC 2018',
    name: 'Good Friday (Siklet) — Orthodox', localName: 'ስቅለት',
    official: {
      start: '2026-04-10', end: '2026-04-10', status: 'OFFICIAL', source: 'ET_PROC_1334',
      note: 'Holiday status is statutory (Art. 6(4)); the DATE is church-computed on the Orthodox reckoning, not set by statute. ⚠️ NOT Western Good Friday, which is 2026-04-03. Strong corroboration: UNECA lists BOTH "3 April 2026 Good Friday" and "10 April 2026 Ethiopian Good Friday" as separate entries.',
    },
    window: { status: 'UNKNOWN', reason: 'No sourced operational window. In practice this is the Fasika workday that is actually lost, since Fasika itself falls on a Sunday in 2026.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2026-fasika', lane: 'SRE', year: 2026, lunar: true, ethiopianYear: 'EC 2018',
    name: 'Fasika — Ethiopian Orthodox Easter', localName: 'ፋሲካ',
    official: {
      start: '2026-04-12', end: '2026-04-12', status: 'OFFICIAL', source: 'ET_PROC_1334',
      note: 'Art. 6(5), Orthodox reckoning. ⚠️ NOT Western Easter, which is 2026-04-05 — a full week earlier. ⚠️ FALLS ON A SUNDAY in 2026, so the legal day off lands on a non-working day; whether a substitute weekday is granted is UNKNOWN (no source found, and Ethiopian labour-law sources are silent on substitution).',
    },
    window: {
      status: 'UNKNOWN',
      reason: 'The 55-day Great Lent (ዐቢይ ጾም) preceding Fasika is a DIETARY fast and nothing in any source indicates it suspends or reduces work. ⚠️ Anyone reading "55-day fast" as a 55-day disruption window would be wrong, and no evidence supports that reading. The all-night Saturday vigil is the only plausible basis for Monday degradation and even that is not sourced.',
    },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2026-labour', lane: 'SRE', year: 2026, lunar: false, ethiopianYear: 'EC 2018',
    name: 'International Labour Day', localName: 'የላብ አደሮች ቀን',
    official: { start: '2026-05-01', end: '2026-05-01', status: 'OFFICIAL', source: 'ET_PROC_1334', note: 'Art. 4(3), Art. 12 — statutory May 01.' },
    window: { status: 'UNKNOWN', reason: 'No sourced operational window beyond the single statutory day.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2026-patriots', lane: 'SRE', year: 2026, lunar: false, ethiopianYear: 'EC 2018',
    name: "Patriots' Victory Day", localName: 'የአርበኞች ቀን',
    official: {
      start: '2026-05-05', end: '2026-05-05', status: 'OFFICIAL', source: 'ET_PROC_1334',
      note: 'Art. 4(4) and Art. 11 — statutory May 05.',
      contradiction: '⚠️ A law-firm brief (ethiopianlaw.com, dated 2024-08-27) states April 6. The proclamation text states May 05 twice and every calendar source says May 5. That brief is demonstrably unreliable — it also lists holidays absent from the proclamation text — so April 6 is treated as its error, not a real alternative.',
    },
    window: { status: 'UNKNOWN', reason: 'No sourced operational window.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2026-eidaladha', lane: 'SRE', year: 2026, lunar: true, ethiopianYear: 'EC 2018',
    name: 'Eid al-Adha (Arefa)', localName: 'አረፋ',
    official: {
      start: '2026-05-27', end: '2026-05-27', status: 'ESTIMATE', source: 'ET_UNECA',
      note: '⚠️ ESTIMATE, ±1 DAY, SUBJECT TO MOON SIGHTING. 10 Dhu al-Hijjah 1447. Statutory under Art. 6(8). UNECA 2026 gives "27 May 2026 Eid-al-Adha (Arafat)*" with the lunar footnote.',
      contradiction: 'holidayapi gives 2026-05-26.',
    },
    window: { status: 'UNKNOWN', reason: 'No source establishes a multi-day stoppage.' },
    severity: 'SINGLE_DAY', confidence: 'medium',
    note: '⚠️ POSSIBLE TWO-DAY GAP: falls the day before Derg Downfall Day (May 28). If both hold, Wed-Thu are both lost with Fri May 29 bridging into the weekend. But this Eid date is ±1 and May 28 is legally disputed, so the collision itself is not certain.',
  },
  {
    id: 'et-2026-dergdownfall', lane: 'SRE', year: 2026, lunar: false, ethiopianYear: 'EC 2018 (Ginbot 20)',
    name: 'Derg Downfall Day', localName: 'ደርግ የወደቀበት ቀን',
    official: {
      start: '2026-05-28', end: '2026-05-28', status: 'DISPUTED', source: 'TIMEANDDATE_ET',
      note: '🔴 LEGAL STATUS DISPUTED — not a silent inclusion and not a silent omission. May 28 appears NOWHERE in Proclamation 1334/2024, neither in the closure lists (Arts. 4, 6) nor the memorial list (Art. 5, which holds only Feb 20 Martyrs\' Day and Dec 9 Nations/Nationalities Day). Yet timeanddate, Wikipedia, publicholidays.africa and the Ethiopian-authored time.et all still list it as a public holiday. The DATE is unambiguous; whether it is a day off is not.',
    },
    window: { status: 'UNKNOWN', reason: 'Legal status itself unresolved, so no window can be stated.' },
    severity: 'UNKNOWN', confidence: 'low',
    action: '⭐ ONE EMAIL TO THE SRE TEAM LEAD RESOLVES THIS, where no public source does. Highest-value open question in the Ethiopian set.',
  },
  {
    id: 'et-2026-mawlid', lane: 'SRE', year: 2026, lunar: true, ethiopianYear: 'EC 2018',
    name: 'Mawlid — Birth of the Prophet', localName: 'መውሊድ',
    official: {
      start: '2026-08-25', end: '2026-08-26', status: 'ESTIMATE', source: 'ET_UNECA',
      note: '🔴 UNRESOLVED BETWEEN TWO SOURCES, reported as a RANGE rather than a pick. 12 Rabi\' al-Awwal 1448. Statutory under Art. 6(6). UNECA (2026-stamped, Addis-based UN office) says 25 August 2026; timeanddate says 26 August 2026 ("Tentative Date"). Both are ±1 and subject to moon sighting. Not picking one.',
    },
    window: { status: 'UNKNOWN', reason: 'No source establishes a multi-day stoppage.' },
    severity: 'SINGLE_DAY', confidence: 'low',
  },
  {
    id: 'et-2026-enkutatash', lane: 'SRE', year: 2026, lunar: false, ethiopianYear: 'opens EC 2019 (Meskerem 1)',
    name: 'Enkutatash — Ethiopian New Year', localName: 'እንቁጣጣሽ',
    official: { start: '2026-09-11', end: '2026-09-11', status: 'OFFICIAL', source: 'ET_PROC_1334', note: 'Art. 4(1), Art. 9 — statutory Sept 11. Opens Ethiopian year 2019 EC. Corroborated by time.et, the German Embassy 2026 table and UNECA 2026.' },
    window: { status: 'UNKNOWN', reason: 'No sourced operational window.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2026-meskel', lane: 'SRE', year: 2026, lunar: false, ethiopianYear: 'EC 2019 (Meskerem 17)',
    name: 'Meskel — Finding of the True Cross', localName: 'መስቀል',
    official: { start: '2026-09-27', end: '2026-09-27', status: 'OFFICIAL', source: 'ET_PROC_1334', note: 'Art. 6(1) — statutory Sept 27. ⚠️ FALLS ON A SUNDAY in 2026; substitute-weekday practice UNKNOWN.' },
    window: {
      start: '2026-09-26', end: '2026-09-27', status: 'SOURCED', sources: ['ET_TIME'],
      note: 'The Demera bonfire is lit on the EVE, and the Addis Ababa Meskel Square Demera draws hundreds of thousands.',
      caveat: '⚠️ No source establishes the eve as a non-working day. Treat as PARTIAL at most.',
    },
    severity: 'SINGLE_DAY', confidence: 'high',
  },

  /* ===================== SRE — ETHIOPIA — 2027 ============================ */
  {
    id: 'et-2027-genna', lane: 'SRE', year: 2027, lunar: false, ethiopianYear: 'EC 2019 (Tahsas 29)',
    name: 'Genna — Ethiopian Orthodox Christmas', localName: 'ገና',
    official: { start: '2027-01-07', end: '2027-01-07', status: 'OFFICIAL', source: 'ET_TIME', note: 'Statutory (Art. 6(2)). 2027 is a clean Jan 7 year — note Genna moves to Jan 8 in 2028 because EC 2019 is an Ethiopian leap year.' },
    window: { status: 'UNKNOWN', reason: 'No source establishes an extended stoppage.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2027-timkat', lane: 'SRE', year: 2027, lunar: false, ethiopianYear: 'EC 2019 (Tir 11)',
    name: 'Timkat — Epiphany', localName: 'ጥምቀት',
    official: { start: '2027-01-19', end: '2027-01-19', status: 'OFFICIAL', source: 'ET_TIME', note: 'Statutory (Art. 6(3)). 2027 is a clean Jan 19 year.' },
    window: { start: '2027-01-18', end: '2027-01-20', status: 'SOURCED', sources: ['ET_ENA_TIMKAT'], note: '3-day festival; only Jan 19 is statutory.', caveat: '⚠️ Window source is UNDATED.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2027-adwa', lane: 'SRE', year: 2027, lunar: false, ethiopianYear: 'EC 2019 (Yekatit 23)',
    name: 'Adwa Victory Day', localName: 'የዓድዋ ድል በዓል',
    official: { start: '2027-03-02', end: '2027-03-02', status: 'OFFICIAL', source: 'ET_TIME', note: 'Statutory March 02 (Art. 4(2)).' },
    window: { status: 'UNKNOWN', reason: 'No sourced evidence of extension.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2027-eidalfitr', lane: 'SRE', year: 2027, lunar: true, ethiopianYear: 'EC 2019 (Megabit 1)',
    name: 'Eid al-Fitr', localName: 'ኢድ አልፈጥር',
    official: { start: '2027-03-10', end: '2027-03-10', status: 'ESTIMATE', source: 'ET_TIME', note: '⚠️ ESTIMATE, ±1 DAY, SUBJECT TO MOON SIGHTING. 1 Shawwal 1448. time.et annotates it "± 1 day"; timeanddate labels it "Tentative Date". Re-verify ~3 weeks out.' },
    window: { status: 'UNKNOWN', reason: 'No source establishes a multi-day stoppage.' },
    severity: 'SINGLE_DAY', confidence: 'low',
  },
  {
    id: 'et-2027-goodfriday', lane: 'SRE', year: 2027, lunar: true, ethiopianYear: 'EC 2019 (Miyazia 22)',
    name: 'Good Friday (Siklet) — Orthodox', localName: 'ስቅለት',
    official: { start: '2027-04-30', end: '2027-04-30', status: 'OFFICIAL', source: 'ET_TIME', note: 'Orthodox reckoning. ⚠️ NOT Western Good Friday, which is 2027-03-26 — over a month earlier.' },
    window: { status: 'UNKNOWN', reason: 'No sourced operational window. This is the Fasika workday actually lost, since Fasika 2027 is a Sunday.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2027-fasika', lane: 'SRE', year: 2027, lunar: true, ethiopianYear: 'EC 2019 (Miyazia 24)',
    name: 'Fasika — Ethiopian Orthodox Easter', localName: 'ፋሲካ',
    official: { start: '2027-05-02', end: '2027-05-02', status: 'OFFICIAL', source: 'ET_TIME', note: 'Orthodox reckoning. ⚠️ NOT Western Easter, which is 2027-03-28 — over a month earlier. ⚠️ FALLS ON A SUNDAY; substitute-weekday practice UNKNOWN.' },
    window: { status: 'UNKNOWN', reason: 'The preceding 55-day Great Lent is a DIETARY fast, not a work stoppage, and is not treated as a window.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2027-labour', lane: 'SRE', year: 2027, lunar: false, ethiopianYear: 'EC 2019 (Miyazia 23)',
    name: 'International Labour Day', localName: 'የላብ አደሮች ቀን',
    official: { start: '2027-05-01', end: '2027-05-01', status: 'OFFICIAL', source: 'ET_TIME', note: 'Statutory May 01. ⚠️ FALLS ON A SATURDAY in 2027; substitute-weekday practice UNKNOWN.' },
    window: { status: 'UNKNOWN', reason: 'No sourced operational window.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2027-patriots', lane: 'SRE', year: 2027, lunar: false, ethiopianYear: 'EC 2019 (Miyazia 27)',
    name: "Patriots' Victory Day", localName: 'የአርበኞች ቀን',
    official: { start: '2027-05-05', end: '2027-05-05', status: 'OFFICIAL', source: 'ET_TIME', note: 'Statutory May 05.' },
    window: { status: 'UNKNOWN', reason: 'No sourced operational window.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2027-eidaladha', lane: 'SRE', year: 2027, lunar: true, ethiopianYear: 'EC 2019 (Ginbot 9)',
    name: 'Eid al-Adha (Arefa)', localName: 'አረፋ',
    official: { start: '2027-05-17', end: '2027-05-17', status: 'ESTIMATE', source: 'ET_TIME', note: '⚠️ ESTIMATE, ±1 DAY, SUBJECT TO MOON SIGHTING. 10 Dhu al-Hijjah 1448. Re-verify ~3 weeks out.' },
    window: { status: 'UNKNOWN', reason: 'No source establishes a multi-day stoppage.' },
    severity: 'SINGLE_DAY', confidence: 'low',
  },
  {
    id: 'et-2027-dergdownfall', lane: 'SRE', year: 2027, lunar: false, ethiopianYear: 'EC 2019 (Ginbot 20)',
    name: 'Derg Downfall Day', localName: 'ደርግ የወደቀበት ቀን',
    official: { start: '2027-05-28', end: '2027-05-28', status: 'DISPUTED', source: 'ET_TIME', note: '🔴 Same dispute as 2026 — absent from Proclamation 1334/2024, present in every calendar including the Ethiopian-authored time.et.' },
    window: { status: 'UNKNOWN', reason: 'Legal status itself unresolved.' },
    severity: 'UNKNOWN', confidence: 'low',
    action: '⭐ Confirm with the SRE team lead.',
  },
  {
    id: 'et-2027-mawlid', lane: 'SRE', year: 2027, lunar: true, ethiopianYear: 'EC 2019 (Nehase 9)',
    name: 'Mawlid — Birth of the Prophet', localName: 'መውሊድ',
    official: { start: '2027-08-15', end: '2027-08-15', status: 'ESTIMATE', source: 'ET_TIME', note: '⚠️ ESTIMATE, ±1 DAY, SUBJECT TO MOON SIGHTING. 12 Rabi\' al-Awwal 1449. ⚠️ Falls on a Sunday in 2027. Re-verify ~3 weeks out.' },
    window: { status: 'UNKNOWN', reason: 'No source establishes a multi-day stoppage.' },
    severity: 'SINGLE_DAY', confidence: 'low',
  },
  {
    id: 'et-2027-enkutatash', lane: 'SRE', year: 2027, lunar: false, ethiopianYear: 'opens EC 2020 (Meskerem 1)',
    name: 'Enkutatash — Ethiopian New Year', localName: 'እንቁጣጣሽ',
    official: { start: '2027-09-12', end: '2027-09-12', status: 'OFFICIAL', source: 'ET_TIME', note: '⚠️ SHIFTS TO SEPT 12 in 2027 — not Sept 11 — because EC 2019 is an Ethiopian leap year. Opens Ethiopian year 2020 EC. This is exactly why a recurring fixed date would be wrong. ⚠️ Falls on a Sunday.' },
    window: { status: 'UNKNOWN', reason: 'No sourced operational window.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'et-2027-meskel', lane: 'SRE', year: 2027, lunar: false, ethiopianYear: 'EC 2020 (Meskerem 17)',
    name: 'Meskel — Finding of the True Cross', localName: 'መስቀል',
    official: { start: '2027-09-28', end: '2027-09-28', status: 'OFFICIAL', source: 'ET_TIME', note: '⚠️ SHIFTS TO SEPT 28 in 2027 — not Sept 27 — same Ethiopian leap-year shift as Enkutatash.' },
    window: { start: '2027-09-27', end: '2027-09-28', status: 'SOURCED', sources: ['ET_TIME'], note: 'Demera bonfire on the eve.', caveat: '⚠️ Eve is not established as a non-working day. PARTIAL at most.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },

  /* ===================== US — 2026 ========================================
   * Dates from OPM's PUBLISHED 2026 schedule — not computed from nth-weekday
   * rules. Windows from the carriers' own published 2026 schedules.
   *
   * ⚠️ TWO THINGS THAT BREAK A NAIVE MODEL, both encoded below:
   * 1. The federal OBSERVED date and the USPS OPERATIONAL closure date are not
   *    the same thing. July 2026 proves it: OPM closed federal offices Fri Jul 3
   *    while USPS worked Fri Jul 3 and closed Sat Jul 4.
   * 2. Parcel and LTL freight diverge — on Jun 19, Nov 27, Dec 24 and Dec 26 in
   *    2026 alone. A single "carrier closed" flag per date would be wrong. */
  {
    id: 'us-2026-newyear', lane: 'US', year: 2026, lunar: false,
    name: "New Year's Day",
    official: { start: '2026-01-01', end: '2026-01-01', status: 'OFFICIAL', source: 'OPM', note: 'Thursday. No weekend shift.' },
    window: { start: '2026-01-01', end: '2026-01-01', status: 'SOURCED', sources: ['UPS_2026', 'USPS_2026'], note: 'UPS closed; USPS closed.', caveat: '⚠️ The FedEx 2026 parcel PDF begins at Jan 19, so FedEx status for 2026-01-01 was NOT established — it would need the 2025 PDF.' },
    severity: 'SINGLE_DAY', confidence: 'medium',
  },
  {
    id: 'us-2026-mlk', lane: 'US', year: 2026, lunar: false,
    name: 'Martin Luther King Jr. Day',
    official: { start: '2026-01-19', end: '2026-01-19', status: 'OFFICIAL', source: 'OPM', note: 'Monday.' },
    window: { start: '2026-01-19', end: '2026-01-19', status: 'SOURCED', sources: ['UPS_2026', 'FEDEX_2026', 'FEDEX_FREIGHT_2026'], note: '⚠️ CARRIERS DIVERGE: UPS CLOSED, FedEx parcel Modified Service, FedEx Freight OPEN, USPS closed.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'us-2026-presidents', lane: 'US', year: 2026, lunar: false,
    name: "Washington's Birthday (Presidents Day)",
    official: { start: '2026-02-16', end: '2026-02-16', status: 'OFFICIAL', source: 'OPM', note: 'Monday. OPM: "it is our policy to always refer to holidays by the names designated in the law" — OPM never says "Presidents Day".' },
    window: { start: '2026-02-16', end: '2026-02-16', status: 'SOURCED', sources: ['UPS_2026', 'FEDEX_2026'], note: 'USPS closed; UPS open; FedEx parcel modified; FedEx Freight open. UPS notes Ground Saver and Mail Innovations "will require one additional business days\' time in transit due to the Federal (USPS) holiday".' },
    severity: 'LIMITED', confidence: 'high',
  },
  {
    id: 'us-2026-memorial', lane: 'US', year: 2026, lunar: false,
    name: 'Memorial Day',
    official: { start: '2026-05-25', end: '2026-05-25', status: 'OFFICIAL', source: 'OPM', note: 'Monday.' },
    window: {
      start: '2026-05-25', end: '2026-05-25', status: 'SOURCED', sources: ['UPS_2026', 'FEDEX_2026', 'FEDEX_FREIGHT_2026', 'USPS_2026'],
      note: 'All four closed — USPS, UPS, FedEx parcel, FedEx Freight.',
      caveat: '⚠️ A multi-day manufacturing shutdown is UNKNOWN — nothing found in any source class. The window is deliberately NOT extended across the long weekend.',
    },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'us-2026-juneteenth', lane: 'US', year: 2026, lunar: false,
    name: 'Juneteenth National Independence Day',
    official: { start: '2026-06-19', end: '2026-06-19', status: 'OFFICIAL', source: 'OPM', note: 'Friday.' },
    window: {
      start: '2026-06-19', end: '2026-06-22', status: 'SOURCED', sources: ['FEDEX_FREIGHT_2026'],
      note: '⚠️ SPLIT: parcel runs (UPS and FedEx both OPEN), USPS closed, but FedEx Freight is on a modified linehaul — "shipments picked up that are not moved will resume transit on the next normal operating day", so LTL picked up Fri Jun 19 does not move until Mon Jun 22.',
      caveat: 'MEDIUM confidence on the exact LTL resume date: the carrier states the rule but gives its worked example for Dec 23, not Jun 19.',
    },
    severity: 'LIMITED', confidence: 'high',
  },
  {
    id: 'us-2026-independence', lane: 'US', year: 2026, lunar: false,
    name: 'Independence Day',
    official: {
      start: '2026-07-03', end: '2026-07-03', status: 'OBSERVED', source: 'OPM',
      statutory: '2026-07-04',
      note: '⚠️ STATUTORY JULY 4 IS A SATURDAY; OPM observes it Friday 2026-07-03 per 5 U.S.C. 6103(b). This is a real shift, not a formality.',
    },
    window: {
      start: '2026-07-03', end: '2026-07-05', status: 'SOURCED', sources: ['USPS_JUL4', 'UPS_2026', 'FEDEX_2026', 'FEDEX_FREIGHT_2026'],
      note: '🔴 THE FEDERAL AND POSTAL DATES DISAGREE, AND BOTH ARE RIGHT. OPM closes federal offices Fri Jul 3. USPS does the OPPOSITE — "Post Offices will be open, and deliveries will occur as normal on Friday, July 3", closing Sat Jul 4. UPS Ground/Air ran Jul 3 but UPS Air & Ocean Freight was CLOSED Jul 3; all UPS closed Jul 4. FedEx parcel Modified Service Jul 3, closed Jul 4. FedEx Freight CLOSED BOTH Jul 3 and Jul 4. Normal resumption Mon Jul 6.',
      caveat: '⚠️ The 1-2 week US summer plant shutdown is a documented pattern but is NOT reliably anchored to the July 4 week — one published manufacturer calendar puts its shutdown Jul 21-28, three weeks later. It is deliberately NOT folded into this holiday\'s window.',
    },
    severity: 'HARD_STOP', confidence: 'high',
  },
  {
    id: 'us-2026-labor', lane: 'US', year: 2026, lunar: false,
    name: 'Labor Day',
    official: { start: '2026-09-07', end: '2026-09-07', status: 'OFFICIAL', source: 'OPM', note: 'Monday.' },
    window: { start: '2026-09-07', end: '2026-09-07', status: 'SOURCED', sources: ['UPS_2026', 'FEDEX_2026', 'FEDEX_FREIGHT_2026', 'USPS_2026'], note: 'All four closed.', caveat: '⚠️ Multi-day shutdown UNKNOWN — nothing found. Window not extended.' },
    severity: 'SINGLE_DAY', confidence: 'high',
  },
  {
    id: 'us-2026-columbus', lane: 'US', year: 2026, lunar: false,
    name: 'Columbus Day',
    official: { start: '2026-10-12', end: '2026-10-12', status: 'OFFICIAL', source: 'OPM', note: 'Monday. OPM says Columbus Day; UPS calls it "Indigenous People\'s Day (Columbus Day)". Naming divergence, not a date divergence.' },
    window: { start: '2026-10-12', end: '2026-10-12', status: 'SOURCED', sources: ['UPS_2026', 'FEDEX_2026'], note: 'USPS closed; UPS open (+1 day on Ground Saver / Mail Innovations); FedEx parcel modified; FedEx Freight open.' },
    severity: 'LIMITED', confidence: 'high',
  },
  {
    id: 'us-2026-veterans', lane: 'US', year: 2026, lunar: false,
    name: 'Veterans Day',
    official: { start: '2026-11-11', end: '2026-11-11', status: 'OFFICIAL', source: 'OPM', note: 'Wednesday — mid-week, so no long-weekend effect.' },
    window: { start: '2026-11-11', end: '2026-11-11', status: 'SOURCED', sources: ['UPS_2026', 'FEDEX_2026'], note: 'USPS closed; UPS open (+1 day on Ground Saver / Mail Innovations); FedEx parcel modified; FedEx Freight open.' },
    severity: 'LIMITED', confidence: 'high',
  },
  {
    id: 'us-2026-thanksgiving', lane: 'US', year: 2026, lunar: false,
    name: 'Thanksgiving',
    official: { start: '2026-11-26', end: '2026-11-26', status: 'OFFICIAL', source: 'OPM', note: 'Thursday.' },
    window: {
      start: '2026-11-25', end: '2026-11-28', status: 'SOURCED', sources: ['FEDEX_2026', 'FEDEX_FREIGHT_2026', 'UPS_2026'],
      note: 'Structured, not uniform: Wed Nov 25 all carriers OPEN but degraded (FedEx early/modified pickups); Thu Nov 26 FULL STOP, all four closed; Fri Nov 27 (Black Friday) parcel RUNS but FedEx Freight CLOSED; Sat Nov 28 FedEx early pickups.',
      caveat: '⚠️ THE "4-DAY THANKSGIVING SHUTDOWN" IS NOT A DOCUMENTED PATTERN — it is two contractual holidays plus an ordinary weekend. Union/company calendars do document Thu+Fri plant closure, but NO official statistical source documents a Thanksgiving-week manufacturing shutdown, and BLS structurally cannot observe it (its reference period is the pay period containing the 12th). No source supports closing the Wednesday before. ⚠️ Also: FedEx parcel on the Wednesday was OPEN in 2025 and Modified in 2026 — read the year\'s PDF, do not carry the rule forward.',
    },
    severity: 'HARD_STOP', confidence: 'high',
  },
  {
    id: 'us-2026-christmas', lane: 'US', year: 2026, lunar: false,
    name: 'Christmas Day',
    official: { start: '2026-12-25', end: '2026-12-25', status: 'OFFICIAL', source: 'OPM', note: 'Friday. No weekend shift.' },
    window: {
      start: '2026-12-23', end: '2027-01-04', status: 'SOURCED', sources: ['FEDEX_FREIGHT_2026', 'UPS_2026', 'FEDEX_2026', 'FED_G17'],
      note: '🔴 THE ONE US WINDOW THAT GENUINELY MEANS TWO WEEKS, and it is sourced end to end. Dec 23: FedEx Freight modified linehaul — "a one-day shipment picked up on Dec. 23 will resume on Dec. 28 and be delivered on Dec. 29" (the carrier\'s own worked example — a 5-day freeze). Dec 24: NO UPS Ground pickup (Air only, prearranged by Dec 18); FedEx Freight CLOSED. Dec 25: all closed. Dec 26: FedEx parcel open, FedEx Freight CLOSED. Dec 31: NO UPS Ground pickup AND no Ground delivery; FedEx Freight prearranged-only, "shipments picked up on Dec. 31 will resume on Jan. 4". Jan 1 2027: all closed. Overlay: FedEx extends commitment times by 90 minutes on select Expedited services from Nov 23 through Jan 4. USPS Christmas send-by: Ground Advantage and First-Class Dec 17, Priority Dec 18, Priority Express Dec 19.',
      manufacturing: 'US manufacturers broadly close Dec 24 → Jan 2. Fed G.17 methodology encodes it ("the shutdowns in the summer and at the end of the year"), and its auto seasonal factors put December persistently far below 100. Corroborated by a UAW-GM contractual holiday block (Dec 24, 25, 26, 29, 30, 31, Jan 1, Jan 2) and a published private-manufacturer shutdown calendar. Confidence medium-high; no BLS confirmation is possible by construction.',
    },
    severity: 'FULL_SHUTDOWN', confidence: 'high',
  },

  /* ===================== US — 2027 ========================================
   * ⚠️ DATES PUBLISHED, DISRUPTION NOT. OPM has published 2027, so every date
   * below is high-confidence and read off a published schedule. But NO carrier
   * has published a 2027 schedule — USPS, UPS and FedEx each publish one
   * calendar year at a time and the latest is 2026. So every 2027 window is
   * UNKNOWN except New Year's Day, which falls inside the 2026 documents.
   *
   * 🔴 2027 IS THE HIGH-SHIFT YEAR: three holidays move off their statutory
   * date. This is precisely why these are not rule-derived. */
  {
    id: 'us-2027-newyear', lane: 'US', year: 2027, lunar: false,
    name: "New Year's Day",
    official: { start: '2027-01-01', end: '2027-01-01', status: 'OFFICIAL', source: 'OPM', note: 'Friday. No shift.' },
    window: {
      start: '2026-12-31', end: '2027-01-04', status: 'SOURCED', sources: ['UPS_2026', 'FEDEX_2026', 'FEDEX_FREIGHT_2026'],
      note: 'THE ONLY 2027 HOLIDAY WITH A SOURCED WINDOW — because it falls inside the published 2026 carrier documents. UPS closed Jan 1; FedEx parcel closed Jan 1, open Jan 2; FedEx Freight closed Jan 1 AND Jan 2, with Dec 31 pickups resuming Jan 4.',
    },
    severity: 'HARD_STOP', confidence: 'high',
  },
  {
    id: 'us-2027-mlk', lane: 'US', year: 2027, lunar: false,
    name: 'Martin Luther King Jr. Day',
    official: { start: '2027-01-18', end: '2027-01-18', status: 'OFFICIAL', source: 'OPM', note: 'Monday.' },
    window: { status: 'UNKNOWN', reason: 'No carrier has published a 2027 holiday schedule. USPS, UPS and FedEx each publish one year at a time; the latest published is 2026.' },
    severity: 'UNKNOWN', confidence: 'high',
    confidenceNote: 'HIGH on the date (OPM published), UNKNOWN on the window.',
  },
  {
    id: 'us-2027-presidents', lane: 'US', year: 2027, lunar: false,
    name: "Washington's Birthday (Presidents Day)",
    official: { start: '2027-02-15', end: '2027-02-15', status: 'OFFICIAL', source: 'OPM', note: 'Monday.' },
    window: { status: 'UNKNOWN', reason: 'No 2027 carrier schedule published.' },
    severity: 'UNKNOWN', confidence: 'high',
  },
  {
    id: 'us-2027-memorial', lane: 'US', year: 2027, lunar: false,
    name: 'Memorial Day',
    official: { start: '2027-05-31', end: '2027-05-31', status: 'OFFICIAL', source: 'OPM', note: 'Monday.' },
    window: { status: 'UNKNOWN', reason: 'No 2027 carrier schedule published.' },
    severity: 'UNKNOWN', confidence: 'high',
  },
  {
    id: 'us-2027-juneteenth', lane: 'US', year: 2027, lunar: false,
    name: 'Juneteenth National Independence Day',
    official: {
      start: '2027-06-18', end: '2027-06-18', status: 'OBSERVED', source: 'OPM', statutory: '2027-06-19',
      note: '⚠️ STATUTORY JUNE 19 IS A SATURDAY; OPM observes Friday 2027-06-18.',
    },
    window: {
      status: 'UNKNOWN',
      reason: 'No 2027 carrier schedule published. ⚠️ AND the Saturday-holiday ambiguity is unresolved: USPS\'s standing rule (ELM 518.21) observes the preceding Friday for PAY AND LEAVE, but the July 2026 precedent proves that does not govern OPERATIONS — USPS worked Friday Jul 3 and closed Saturday Jul 4. Which day USPS actually closes is not pre-fillable.',
    },
    severity: 'UNKNOWN', confidence: 'high',
  },
  {
    id: 'us-2027-independence', lane: 'US', year: 2027, lunar: false,
    name: 'Independence Day',
    official: {
      start: '2027-07-05', end: '2027-07-05', status: 'OBSERVED', source: 'OPM', statutory: '2027-07-04',
      note: '⚠️ STATUTORY JULY 4 IS A SUNDAY; OPM observes Monday 2027-07-05.',
    },
    window: { status: 'UNKNOWN', reason: 'No 2027 carrier schedule published.' },
    severity: 'UNKNOWN', confidence: 'high',
  },
  {
    id: 'us-2027-labor', lane: 'US', year: 2027, lunar: false,
    name: 'Labor Day',
    official: { start: '2027-09-06', end: '2027-09-06', status: 'OFFICIAL', source: 'OPM', note: 'Monday.' },
    window: { status: 'UNKNOWN', reason: 'No 2027 carrier schedule published.' },
    severity: 'UNKNOWN', confidence: 'high',
  },
  {
    id: 'us-2027-columbus', lane: 'US', year: 2027, lunar: false,
    name: 'Columbus Day',
    official: { start: '2027-10-11', end: '2027-10-11', status: 'OFFICIAL', source: 'OPM', note: 'Monday.' },
    window: { status: 'UNKNOWN', reason: 'No 2027 carrier schedule published.' },
    severity: 'UNKNOWN', confidence: 'high',
  },
  {
    id: 'us-2027-veterans', lane: 'US', year: 2027, lunar: false,
    name: 'Veterans Day',
    official: { start: '2027-11-11', end: '2027-11-11', status: 'OFFICIAL', source: 'OPM', note: 'Thursday.' },
    window: { status: 'UNKNOWN', reason: 'No 2027 carrier schedule published.' },
    severity: 'UNKNOWN', confidence: 'high',
  },
  {
    id: 'us-2027-thanksgiving', lane: 'US', year: 2027, lunar: false,
    name: 'Thanksgiving',
    official: { start: '2027-11-25', end: '2027-11-25', status: 'OFFICIAL', source: 'OPM', note: 'Thursday.' },
    window: { status: 'UNKNOWN', reason: 'No 2027 carrier schedule published. The 2026 shape is NOT carried forward — FedEx parcel status on the Wednesday and on Black Friday both changed between 2025 and 2026, so last year\'s pattern is not evidence for next year.' },
    severity: 'UNKNOWN', confidence: 'high',
  },
  {
    id: 'us-2027-christmas', lane: 'US', year: 2027, lunar: false,
    name: 'Christmas Day',
    official: {
      start: '2027-12-24', end: '2027-12-24', status: 'OBSERVED', source: 'OPM', statutory: '2027-12-25',
      note: '⚠️ STATUTORY DECEMBER 25 IS A SATURDAY; OPM observes Friday 2027-12-24.',
    },
    window: {
      status: 'UNKNOWN',
      reason: 'No 2027 carrier schedule published, AND the Saturday-holiday operational ambiguity is unresolved (see Juneteenth 2027). ⚠️ Compounding: Dec 31 2027 is ALSO a federal holiday — New Year\'s Day 2028 falls on a Saturday so OPM observes it Fri 2027-12-31. The 2027 year-end therefore carries TWO Friday federal holidays, which will likely produce a longer-than-usual freeze — but there is no source for its carrier shape and none is estimated here.',
    },
    severity: 'UNKNOWN', confidence: 'high',
  },
  {
    id: 'us-2027-newyear-2028-observed', lane: 'US', year: 2027, lunar: false,
    name: "New Year's Day 2028 (observed early)",
    official: { start: '2027-12-31', end: '2027-12-31', status: 'OBSERVED', source: 'OPM', statutory: '2028-01-01', note: 'Jan 1 2028 is a Saturday, so OPM observes it Friday 2027-12-31. Included because it lands inside the covered period and would otherwise be a silent omission in the 2027 year-end.' },
    window: { status: 'UNKNOWN', reason: 'No 2027 carrier schedule published.' },
    severity: 'UNKNOWN', confidence: 'high',
  },
]

/* ---- China make-up working days ----------------------------------------- */

/**
 * 🔴 THE INVERSE TRAP: weekends on which JD is OPEN.
 *
 * China's holiday notice pairs long breaks with 调休 — mandated make-up working
 * weekends. Three independent sources flag that Western holiday feeds drop
 * these. If a calendar renders weekends as non-working, these days render wrong
 * in the OPPOSITE direction from the holidays: showing closed when JD is open.
 *
 * 2026 only. There are no 2027 entries because the 2027 notice is unpublished —
 * and that absence is itself information, not an empty list.
 */
export const CN_MAKEUP_WORKDAYS = [
  { date: '2026-01-04', forHoliday: 'New Year', source: 'CN_SC_2026' },
  { date: '2026-02-14', forHoliday: 'Spring Festival', source: 'CN_SC_2026' },
  { date: '2026-02-28', forHoliday: 'Spring Festival', source: 'CN_SC_2026' },
  { date: '2026-05-09', forHoliday: 'Labour Day', source: 'CN_SC_2026' },
  { date: '2026-09-20', forHoliday: 'National Day', source: 'CN_SC_2026' },
  { date: '2026-10-10', forHoliday: 'National Day', source: 'CN_SC_2026' },
]

/* ---- what this data cannot tell you ------------------------------------- */

/**
 * Stated in the data file rather than discovered later by whoever trusts it.
 */
export const KNOWN_GAPS = [
  {
    id: 'jd-own-schedule',
    severity: 'blocking-for-ship-dates',
    gap: "JD's OWN written holiday and shutdown schedule is not held anywhere in SR.",
    why: 'Every China source independently says the published national calendar is not the factory\'s calendar. Silk Way: "Get each factory\'s annual holiday schedule in writing, and re-ask every year." Sorva: "ask your supplier directly for its closing and reopening dates rather than rely on a generic calendar." Titoma: "Individual factory shutdown schedules may differ."',
    toClose: 'Ask JD for its 2026 and 2027 holiday schedule in writing. Until then the Spring Festival window on this calendar is an industry pattern, not JD\'s actual downtime.',
  },
  {
    id: 'cn-2027-unpublished',
    severity: 'resolves-on-its-own',
    gap: 'Every 2027 Chinese date and block is UNKNOWN.',
    why: 'The State Council 2027 放假安排 notice does not exist as of 2026-10-04. More searching will not fix it.',
    toClose: 'Re-run this research when the notice publishes, expected late Oct / Nov / Dec 2026 — roughly 4-10 weeks from the research date.',
  },
  {
    id: 'us-2027-carriers',
    severity: 'resolves-on-its-own',
    gap: 'Every 2027 US disruption window is UNKNOWN except New Year\'s Day.',
    why: 'USPS, UPS and FedEx publish one calendar year at a time; none has published 2027.',
    toClose: 'Re-pull the three carrier schedules when they publish, typically late in the preceding year.',
  },
  {
    id: 'et-may28',
    severity: 'one-email',
    gap: 'Derg Downfall Day (May 28) — whether it is actually a day off for SRE.',
    why: 'Absent from Proclamation 1334/2024 entirely; present in every secondary calendar including Ethiopian-authored ones.',
    toClose: '⭐ Ask the SRE team lead. One email resolves what no public source does.',
  },
  {
    id: 'et-islamic-estimates',
    severity: 'recurring',
    gap: 'All six Islamic dates (2026 and 2027) are astronomical ESTIMATES, ±1 day.',
    why: 'Confirmed by moon sighting, often announced only days ahead. No official Ethiopian declaration was found for any of them. Mawlid 2026 is additionally unresolved between two sources (Aug 25 vs Aug 26).',
    toClose: 'Re-verify each roughly 3 weeks before it falls. They are rendered as ESTIMATE, never as fact.',
  },
  {
    id: 'et-weekend-substitution',
    severity: 'unknown',
    gap: 'Whether an Ethiopian holiday falling on a weekend generates a substitute weekday off.',
    why: 'No source found. Ethiopian labour-law sources cover annual leave and holiday double-pay but are silent on substitution.',
    affects: 'Fasika 2026 (Sun), Meskel 2026 (Sun), Labour Day 2027 (Sat), Fasika 2027 (Sun), Mawlid 2027 (Sun), Enkutatash 2027 (Sun).',
    toClose: 'Ask the SRE team lead alongside the May 28 question.',
  },
  {
    id: 'delivery-window-link',
    severity: 'blocking-for-collision',
    gap: 'SR cannot currently compute a championship\'s DELIVERY window, so the calendar cannot flag a true delivery-vs-shutdown collision.',
    why: 'The Command Center calendar renders championship records keyed on `effDate` — the EVENT date. Production and delivery live on a different object: the ClickUp journey/production order (list 901000306889), modelled in `src/lib/journey.js` as 14 stages off `mfg_release`. Nothing joins a championship record to its production order on this surface, and `In Hands Date` on `/api/clickup/tasks` is an OPEN ASK on BUILD LOG 868k89xtr ("Expose Total Revenue, Total Units and In Hands Date on /api/clickup/tasks returning null not 0").',
    toClose: 'Two things, in order: (1) expose In Hands Date on /api/clickup/tasks as a real value rather than null — already requested on the BUILD LOG; (2) provide a join from a championship record to its production order. With both, the collision becomes a real delivery-window calculation. WITHOUT them, no lead-time assumption should be substituted — see `EVENT_DATE_COLLISION_IS_NOT_A_DELIVERY_COLLISION`.',
  },
]

/**
 * 🔴 READ THIS BEFORE TRUSTING THE COLLISION FLAG.
 *
 * What the calendar CAN honestly flag today is that a championship's EVENT DATE
 * falls inside a disruption window. That is real: the event date is sourced data
 * on the record, and the window is sourced data in this file.
 *
 * What it CANNOT flag is that a championship's DELIVERY or IN-HANDS date
 * collides with a shutdown — because SR does not currently hold a delivery date
 * on this surface (see the `delivery-window-link` gap).
 *
 * These are different claims and the UI says which one it is making. Deriving a
 * delivery date by adding a lead time to the event date would look like the
 * feature Brian asked for and would be a fabrication: `journey.js` measures its
 * 14 production stages from `mfg_release`, which is NOT the event date, and no
 * source in this repo maps one to the other.
 */
export const EVENT_DATE_COLLISION_IS_NOT_A_DELIVERY_COLLISION = true
